import { axios, fetchWithHeaders } from '@sendible/common';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getUrlWithCredentialsFromUser } from '../../utils';
import endpoints from '../../data-layer/endpoints';
import { fileSizeLimits, supportedFileTypes } from '../medias/consts';
import { MediaFileType } from '../medias/types';

type TranslationParamsType = {
  fileType?: string;
  sizeLimit?: string;
  category?: string;
};

export const uploadParts = (
  uploadIntent: MediaUploadIntentType,
  file: File,
  fileId?: string,
  onProgress?: (fileId: string, progress: number) => void
): { uploadPromise: Promise<MediaUploadedPartType[]>; cancel: () => void } => {
  const {
    upload: { parts },
  } = uploadIntent;
  const progressTracker: number[] = [];
  const abortController = new AbortController();

  // Helper to keep track of byte ranges for each part
  let partStartByte = 0;
  const getPartBlob = (part: MediaUploadPartType) => {
    const { partSize } = part;
    const partEndByte = partStartByte + partSize;
    const partialBlob = file.slice(partStartByte, partEndByte);

    partStartByte = partEndByte;

    return partialBlob;
  };

  const upload = (part: MediaUploadPartType): Promise<MediaUploadedPartType> => {
    const { url, partNumber } = part;
    const partialBlob = getPartBlob(part);

    return axios
      .put(url, partialBlob, {
        headers: { 'content-type': file.type },
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent?.total) {
            progressTracker[partNumber - 1] = Math.round((progressEvent.loaded * 100) / progressEvent.total);

            const uploadProgress = progressTracker.reduce((sum, progress) => sum + progress, 0) / parts.length;

            if (onProgress && fileId) onProgress(fileId, uploadProgress);
          }
        },
      })
      .then((uploadedPartResponse) => {
        if (uploadedPartResponse && uploadedPartResponse.status === 200 && uploadedPartResponse?.headers) {
          const eTag = uploadedPartResponse.headers.etag;

          if (eTag) {
            return {
              eTag: eTag.replaceAll('"', ''),
              partNumber,
            };
          }
        }
        throw new Error('Failed to upload part');
      });
  };

  const runConcurrent = async <T, TR>(items: T[], worker: (item: T, index: number) => Promise<TR>, concurrency: number): Promise<TR[]> => {
    const results: TR[] = [];
    let nextIndex = 0;
    let hasError = false;

    async function workerLoop() {
      while (!hasError && nextIndex < items.length) {
        const current = nextIndex++;

        try {
          results[current] = await worker(items[current], current);
        } catch (err) {
          hasError = true;
          throw err;
        }
      }
    }

    await Promise.all(Array.from({ length: concurrency }, workerLoop));

    return results;
  };

  const maxConcurrency = 10;
  const uploadPromise = runConcurrent(parts, (part) => upload(part), maxConcurrency);

  const cancel = () => {
    abortController.abort();
  };

  return { uploadPromise, cancel };
};

export const completeUpload = (uploadIntent: MediaUploadIntentType, parts: MediaUploadedPartType[], user: UseUploadUser) => {
  return fetchWithHeaders({
    method: endpoints.CompleteUploadMedias.method,
    url: getUrlWithCredentialsFromUser(endpoints.CompleteUploadMedias.endpoint, user),
    body: {
      id: uploadIntent.id,
      uploadedParts: parts,
    },
    headers: { 'Content-Type': 'application/json' },
  });
};

export const useErrorCodeMessageFinder = (uploadingMedia: UploadingMediaType[]) => {
  const { t } = useTranslation('compose_box');

  const errorCodeMap = useMemo(
    () => ({
      UnsupportedContentType: (params: TranslationParamsType) => {
        switch (params.category) {
          case 'image':
            return t('UnsupportedImageFormats');
          case 'video':
            return t('UnsupportedVideoFormats');
          case 'document':
          case 'pdf':
            return t('UnsupportedDocumentFormats');
          default:
            return t('UnsupportedUpload');
        }
      },
      UploadTooLarge: (params: TranslationParamsType) => {
        if (params.fileType && params.sizeLimit) {
          return t('UploadTooLarge', { fileType: params.fileType || 'media', sizeLimit: params.sizeLimit || '100MB' });
        }

        return t('UploadTooLargeUnknownType');
      },
      CompleteFailed: () => t('WeCouldntRetrieveYourMedia'),
      AbortFailed: () => t('AbortFailed'),
      FileNotFound: () => t('WeCouldntRetrieveYourMedia'),
      InvalidUploadUrl: () => t('WeCouldntRetrieveYourMedia'),
      InvalidUploadSource: () => t('WeCouldntRetrieveYourMedia'),
      UploadFailed: () => t('WeCouldntProcessYourMedia'),
      ProcessingFailed: () => t('WeCouldntProcessYourMedia'),
      Cancelled: () => t('UploadCancelled'),
      MissingOriginalFile: () => t('WeCouldntProcessYourMedia'),
      MediaNotReady: () => t('WeCouldntProcessYourMedia'),
      MediaCreationFailed: () => t('WeCouldntProcessYourMedia'),
      Unknown: () => t('WeCouldntProcessYourMedia'),
      Processing: () => t('WeCouldntProcessYourMedia'),
      UnsupportedResolution: () => t('UnsupportedResolution'),

      // TODO: Delete these once there are no more media/files that use them
      FileUploadFailed: () => t('WeCouldntProcessYourMedia'),
      FileProcessingFailed: () => t('WeCouldntProcessYourMedia'),
      FileUploadCancelled: () => t('UploadCancelled'),
    }),
    [t]
  );

  const getTranslationParams = (params: UploadOnErrorArgsType) => {
    const translationParams: TranslationParamsType = { fileType: undefined, sizeLimit: undefined, category: undefined };
    const { file, mediaId } = params;
    const mediaIdNumber = typeof mediaId === 'string' ? Number(mediaId) : mediaId;
    const media = uploadingMedia.find((m) => mediaIdNumber !== undefined && m.id === mediaIdNumber);

    if (media) {
      if (Object.values(MediaFileType).includes(media.type)) {
        translationParams.category = media.type.toLowerCase();
        translationParams.fileType = media.type.toLowerCase();
        translationParams.sizeLimit = fileSizeLimits[media.type];
      }
    } else if (file) {
      const matchedEntry = Object.entries(supportedFileTypes).find(([, prefix]) => prefix && file.type.toLowerCase().startsWith(prefix));
      const fileType = matchedEntry?.[0];
      const sizeLimit = fileSizeLimits[fileType as keyof typeof fileSizeLimits];

      if (fileType) {
        translationParams.category = fileType.toLowerCase();
        translationParams.fileType = `${fileType.toLowerCase()}s`;
        translationParams.sizeLimit = sizeLimit;
      }
    }

    return translationParams;
  };

  type ErrorCodeKeys = keyof typeof errorCodeMap;

  return (errorCode: string, params: UploadOnErrorArgsType) => {
    const translationParams = getTranslationParams(params);
    const translateFunction = errorCodeMap[errorCode as ErrorCodeKeys];
    const message = translateFunction ? translateFunction(translationParams) : t('UnknownError');

    return message;
  };
};
